import { ENV, REGION } from 'src/core/config/APIConstants';
import { getEnv, getRegion } from 'src/utils/Environment';

/**
 * Blanket DUB (EU / eu-west-1) -> ZAZ (eu-south-2) operator redirect, draining the
 * EU-region Newton home to ZAZ across BOTH production front doors.
 *
 * Newton Web is a Katal/React SPA, so this is a SPA-bootstrap redirect rather than an
 * HTTP 302: {@link runRegionRedirect} runs once before React mounts and does a full
 * `location.replace()` to the matching ZAZ host.
 *
 * The redirect stays WITHIN the operator's own host family (see {@link zazHostFor}) —
 * crossing families would strand them at an auth door they cannot pass:
 *  - Midway `prod.eu.newton.aft.amazon.dev` -> `prod.zaz.newton.aft.amazon.dev`
 *  - AFT-X  `newton.eu.aftx.amazonoperations.app` -> `newton.zaz.aftx.amazonoperations.app`
 *
 * Production + EU only. NA / NRT are untouched and ZAZ itself is skipped, which is what
 * makes this loop-safe: `getRegion` matches `zaz.` on both ZAZ hosts, so neither is EU.
 *
 * AFT-X was excluded in CR-284242926 on the grounds that FC Menu region-routes that door.
 * It does not: `TupacMenuEntryLoader.getOverrideUrl()` resolves the AFTX branch from
 * `AFTX-<warehouseId>` only, with no region cascade, so flipping FCCS `aws_region` does
 * not move that URL. The AFT-X door does, however, have a regional Odin/KMS dependency in
 * its API Gateway authorizer — see the pre-deploy gates on this CR.
 *
 * Every EU-door request is redirected regardless of FC. An earlier revision gated on a
 * bundled FC allowlist; reintroduce a per-FC gate here if a gradual cutover is needed.
 */

/** The DUB -> ZAZ redirect target host on the Midway front door. */
export const ZAZ_REDIRECT_HOST = 'prod.zaz.newton.aft.amazon.dev';

/** The DUB -> ZAZ redirect target host on the AFT-X front door. */
export const ZAZ_AFTX_REDIRECT_HOST = 'newton.zaz.aftx.amazonoperations.app';

/**
 * Query param appended to the ZAZ target URL so the ZAZ page can tell the
 * operator they were redirected (see `RegionRedirectBanner`). DUB and ZAZ are
 * different origins, so the URL is the only reliable cross-door carrier for
 * this signal. Consumed (stripped) by {@link consumeRedirectMarker} before
 * React mounts, so it never survives into bookmarks, shares, or refreshes.
 */
export const REDIRECT_SOURCE_PARAM = 'redirectedFrom';

/** Marker value identifying the DUB (EU) door as the redirect source. */
export const REDIRECT_SOURCE_VALUE = 'dub';

/** The source region being drained to ZAZ: EU (Dublin / eu-west-1). */
export const DUB_REGION: REGION = REGION.EU;

/** True for the AFT-X front door (as opposed to the Midway front door). */
const isAftxHost = ( host: string ): boolean => host.endsWith( '.aftx.amazonoperations.app' );

/**
 * Selects the ZAZ target host in the SAME front-door family as `host`. Deliberately does
 * not consult the FC Menu URL: that login door is EU-only (no ZAZ AFT-X *menu* host) and
 * is reached separately with a `?referrer=` back to the current page.
 */
const zazHostFor = ( host: string ): string =>
    isAftxHost( host ) ? ZAZ_AFTX_REDIRECT_HOST : ZAZ_REDIRECT_HOST;

/**
 * Builds the ZAZ target URL for the given target host, preserving the
 * operator's current path, query string, and hash fragment exactly, and
 * appending the {@link REDIRECT_SOURCE_PARAM} marker so the ZAZ page can show
 * a "you have been redirected" banner.
 */
const buildTargetUrl = ( targetHost: string, path: string, query?: string, hash?: string ): string =>
{
    const normalizedPath = path.startsWith( '/' ) ? path : `/${path}`;
    const normalizedQuery = query && query.length > 0
        ? ( query.startsWith( '?' ) ? query : `?${query}` )
        : '';
    const marker = `${REDIRECT_SOURCE_PARAM}=${REDIRECT_SOURCE_VALUE}`;
    const queryWithMarker = normalizedQuery.length > 0
        ? `${normalizedQuery}&${marker}`
        : `?${marker}`;
    const normalizedHash = hash && hash.length > 0
        ? ( hash.startsWith( '#' ) ? hash : `#${hash}` )
        : '';

    return `https://${targetHost}${normalizedPath}${queryWithMarker}${normalizedHash}`;
};

/**
 * Consumes (reads and strips) the {@link REDIRECT_SOURCE_PARAM} marker from
 * the current URL. Runs once before React mounts.
 *
 * @param location injectable for tests; defaults to `window.location`.
 * @param historyApi injectable for tests; defaults to `window.history`.
 * @returns `true` if the operator landed here via the DUB -> ZAZ redirect.
 */
export const consumeRedirectMarker = (
    location: Location = window.location,
    historyApi: History = window.history,
): boolean =>
{
    const params = new URLSearchParams( location.search );
    if ( params.get( REDIRECT_SOURCE_PARAM ) !== REDIRECT_SOURCE_VALUE ) return false;

    // Strip the marker so it never survives into bookmarks, shared links, or
    // refreshes (a refresh should not re-show the banner).
    params.delete( REDIRECT_SOURCE_PARAM );
    const remaining = params.toString();
    historyApi.replaceState( null, '', `${location.pathname}${remaining ? `?${remaining}` : ''}${location.hash ?? ''}` );

    return true;
};

/**
 * SPA-bootstrap DUB (EU) -> ZAZ redirect. Runs once before React mounts; on any
 * production EU page — Midway door or AFT-X door — performs a full-page redirect
 * to the corresponding ZAZ host in the same door family, so the entire session
 * moves to ZAZ.
 *
 * No-ops — returns `false` — when either guard fails:
 *  - non-production env (ZAZ is production-only, incl. the AFT-X `.integ.` beta host),
 *  - the operator is not on an EU door (NA / NRT stay; ZAZ is already there).
 *
 * @param location injectable for tests; defaults to `window.location`.
 * @returns `true` if a redirect was triggered (caller should halt bootstrap).
 */
export const runRegionRedirect = ( location: Location = window.location ): boolean =>
{
    // Production + EU only. Skips ZAZ itself on both families, since `getRegion` matches
    // `zaz.` to REGION.ZAZ !== DUB_REGION — this is what makes it loop-safe.
    if ( getEnv( location.host ) === ENV.PRODUCTION
        && getRegion( location.host, ENV.PRODUCTION ) === DUB_REGION )
    {
        location.replace( buildTargetUrl(
            zazHostFor( location.host ),
            location.pathname,
            location.search,
            location.hash,
        ) );
        return true;
    }

    return false;
};
